import { DexArticleService } from './dex/dex-article.service';
import { DexDiscursiveExerciseService } from './dex/dex-discursive-exercise.service';
import { DexObjectiveExerciseService } from './dex/dex-objective-exercise.service';
import {
  GetDiscursiveExerciseInfoFromDexType,
  GetObjectiveExerciseInfoFromDexType,
  GetArticleInfoFromDexType,
  CreateReplyResponse,
} from '@/models/undergrad/services/lessons-reader.model';
import {
  AssertionsByQuestionIdListType,
  DexCreateReply,
  RepliesByQuestionIdListType,
  SolutionsByQuestionId,
} from '@/models/undergrad/services/dex.services.model';
import { DexReplyService } from './dex/dex-reply.service';
import { Lesson, ResponseLessonItem } from '@/models/undergrad/services/disciplines-reader.model';
import { StellateLessonsReaderService } from './stellate/stellate-lessons-reader.service';
import { LessonItemType } from '@/models/undergrad/undegrad-lesson.model';

export class LessonReaderService {
  public static async getDiscursiveExerciseInfoFromDex(
    exerciseId: number,
    userId: number,
  ): Promise<GetDiscursiveExerciseInfoFromDexType> {
    const response = await DexDiscursiveExerciseService.getDiscursiveExerciseById(
      exerciseId,
      userId,
    );

    const contents = response.contents.map((content) => {
      const { textByTextId, imageByImageId, videoByVideoId } = content;

      return {
        text: textByTextId?.body,
        image: imageByImageId,
        video: videoByVideoId,
      };
    });

    return { comments: response.comments, contents };
  }

  public static async getObjectiveExerciseInfoFromDex(
    exerciseId: number,
    userId: number,
  ): Promise<GetObjectiveExerciseInfoFromDexType> {
    const response = await DexObjectiveExerciseService.getObjectiveExerciseById(exerciseId, userId);

    const contents = response.contents.map((content) => {
      const { textByTextId, imageByImageId, videoByVideoId } = content;

      return {
        text: textByTextId?.body,
        image: imageByImageId,
        video: videoByVideoId,
      };
    });

    const exerciseInfoObject: GetObjectiveExerciseInfoFromDexType = {
      contents,
      alternatives: [],
      replies: [],
      solutions: [],
      comments: [],
    };

    if (response.alternatives) {
      exerciseInfoObject.alternatives = response.alternatives.map(
        (alternative: AssertionsByQuestionIdListType) => {
          const alternativeContents = alternative.contentsByAssertionIdList
            .sort((a, b) => a.position - b.position)
            .map((content) => ({
              text: content.textByTextId?.body,
              image: content.imageByImageId,
              video: content.videoByVideoId,
            }));

          return {
            id: alternative.id,
            position: alternative.position,
            correct: alternative.correct,
            contents: alternativeContents,
          };
        },
      );
    }

    if (response.replies) {
      exerciseInfoObject.replies = response.replies.map((reply: RepliesByQuestionIdListType) => ({
        id: reply.id,
        alternativeId: reply.alternativeId,
      }));
    }

    if (response.solutions) {
      exerciseInfoObject.solutions = response.solutions.map((solution: SolutionsByQuestionId) => {
        const solutionContents = solution.contentsBySolutionIdList
          .sort((a, b) => a.position - b.position)
          .map((content) => ({
            text: content.textByTextId?.body,
            image: content.imageByImageId,
            video: content.videoByVideoId,
          }));

        return {
          id: solution.id,
          user: solution.userByUserId,
          contents: solutionContents,
        };
      });
    }

    exerciseInfoObject.comments = response.comments;

    return exerciseInfoObject;
  }

  public static async getArticleInfoFromDex(
    articleId: number,
  ): Promise<GetArticleInfoFromDexType[]> {
    const contents = await DexArticleService.getDexArticleInfo(articleId);

    return contents.map((content) => {
      const { textByTextId, imageByImageId, videoByVideoId } = content;

      return {
        text: textByTextId?.body,
        image: imageByImageId,
        video: videoByVideoId,
      };
    });
  }

  public static async createReply(payload: DexCreateReply): Promise<CreateReplyResponse> {
    const { reply, solutions } = await DexReplyService.createReply(payload);

    return {
      reply: {
        id: reply.id,
        alternativeId: reply.alternativeId,
      },
      solutions: solutions.map((solution: SolutionsByQuestionId) => {
        const solutionContents = solution.contentsBySolutionIdList
          .sort((a, b) => a.position - b.position)
          .map((content) => ({
            text: content.textByTextId?.body,
            image: content.imageByImageId,
            video: content.videoByVideoId,
          }));

        return {
          id: solution.id,
          user: solution.userByUserId,
          contents: solutionContents,
        };
      }),
    };
  }

  public static async getLessonBySlug(slug: string): Promise<Lesson> {
    const lesson = await StellateLessonsReaderService.getLessonBySlug(slug);

    lesson.items = lesson.items.map((item): ResponseLessonItem => {
      return {
        ...item,
        typename: item.typename.replace('Facul', '') as LessonItemType,
      };
    });

    return lesson;
  }
}
